import {atom} from 'jotai';
import {atomWithStorage, createJSONStorage} from 'jotai/utils';
import {fetchAuthSession, getCurrentUser, signIn as amplifySignIn, signOut as amplifySignOut} from 'aws-amplify/auth';
import {isFieldRole, RoleType} from '@/types/auth';
import {getOnlineState, setOnlineState} from '@/lib/onlineState';
import {enableOfflineCachePersistence, apolloClient} from '@/lib/apolloClient';
import {User_GetMyDetailsDocument} from '@/graphql/generated/graphql.ts';
import {ADMIN_GROUP, SUPER_ADMIN_GROUP} from '@/Constants.ts';

export type AuthStatus = 'loading' | 'authenticated' | 'offline-cached' | 'unauthenticated';

// History of every attempt at a backend-sourced user profile on the
// session, all of which used to fail server-side:
//   1. user_GetMyDetails — backend resolver used to throw for this
//      zero-argument query ("Cannot create property 'where' on string
//      'user_GetMyDetails'").
//   2. user_ByCognitoSub(cognitoSub) — backend resolver used to throw too
//      ("The argument passed to findOne must be an options object, use
//      findByPk if you wish to pass a single primary key value" — a
//      Sequelize misuse, findOne(value) instead of
//      findOne({where: {...}})).
//   3. user_AllWhere(userSearch: {username}) — also failed server-side
//      (same class of bug as the two above).
//   4. user_All, filtered to the matching username client-side — worked
//      for a while, then started failing too.
// Both #1 and #2 are confirmed fixed now, so fetchMyUserProfile below
// uses #1 (user_GetMyDetails) rather than #2: it's a zero-argument
// "get the caller's own record" query resolved off the auth context
// server-side, so unlike user_ByCognitoSub it doesn't depend on the
// client supplying a cognitoSub that has to match whatever value the
// backend stored byte-for-byte — one less thing that can silently
// mismatch between Cognito and the User table. Still degrades
// gracefully — if it throws (or returns nothing), firstName/lastName/
// employeeId/email fall back to deriveNameFromToken's Cognito ID token
// claims (no GraphQL call), same as before.
export interface CachedSession {
   username: string;
   userId: string;
   firstName: string;
   lastName: string;
   /** '' if user_GetMyDetails fails — see the comment above. */
   employeeId: string;
   /** '' if user_GetMyDetails fails — see the comment above. */
   email: string;
   role: RoleType;
   /** ISO timestamp of the last time this session was verified against Cognito. */
   lastVerifiedAt: string;
}

/**
 * How long a cached session is trusted for offline use before we
 * require the user to reconnect and re-verify. Adjust to your org's
 * security posture (field crews on well sites may need this longer).
 */
const OFFLINE_GRACE_PERIOD_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

const storage = createJSONStorage<CachedSession | null>(() => localStorage);

/**
 * The last-known-good session, persisted to localStorage so the app
 * can present an "offline-cached" authenticated state when there is
 * no network and Cognito can't be reached (e.g. field work at a well
 * site with no signal).
 */
export const cachedSessionAtom = atomWithStorage<CachedSession | null>('well-data-app:cached-session', null, storage);

const isOnlineBaseAtom = atom<boolean>(getOnlineState());

// ADDED 2026-08-31 (Duane): "offline detection is not working
// correctly." isOnlineAtom used to be a plain atom seeded once from
// navigator.onLine, updated only by useAuthBootstrap.ts's browser
// event listeners. Non-React code (apolloClient.ts's timeoutLink)
// couldn't read it without a circular import (this file already
// imports apolloClient.ts for fetchMyUserProfile's apolloClient.query
// call), so it fell back to reading navigator.onLine directly there —
// meaning the two could disagree, and either one going stale (see
// lib/onlineState.ts's own top comment on why the browser's own flag
// isn't trustworthy on its own) left some part of the app timing out
// against the wrong ceiling. This read/write atom is now a thin Jotai
// wrapper around lib/onlineState.ts's plain module state: every
// `set(isOnlineAtom, value)` (useAuthBootstrap's events/poll — the
// only writer) updates BOTH the atom (for React
// components — TopNav/BottomNav's chips and sync icon, WellsListPage/
// DailyReportPage's loading gates, useOfflineSync/useOfflineMutation)
// and lib/onlineState.ts's plain flag (for apolloClient.ts, which
// reads it synchronously per-request with no React/Jotai dependency
// at all) in one call, so there's exactly one source of truth instead
// of two that can drift apart.
export const isOnlineAtom = atom(
   (get) => get(isOnlineBaseAtom),
   (_get, set, value: boolean) => {
      setOnlineState(value);
      set(isOnlineBaseAtom, value);
   },
);

export const authStatusAtom = atom<AuthStatus>('loading');

/**
 * Pulls the permission role out of the current Cognito session's ID
 * token's group claim. Role stays Cognito-group-based on purpose: it's a
 * different concept from the User table's own roleList (see the TODO on
 * that field in User_GetMyDetails.graphql), and RoleType is what every
 * permission check in this app (isAdminOrSuperAdmin, isFieldRole, nav
 * gating, ...) actually keys off.
 *
 * NOTE: the group names ('admin' / 'superadmin') below are still
 * placeholders — swap in whatever your actual Cognito group setup uses.
 */
async function deriveRole(): Promise<RoleType> {
   const authSession = await fetchAuthSession();
   const claims = authSession.tokens?.idToken?.payload ?? {};
   const groups = (claims['cognito:groups'] as string[] | undefined) ?? [];

   const isAdmin = groups.includes(ADMIN_GROUP);
   const isSuperAdmin = groups.includes(SUPER_ADMIN_GROUP);

   return isAdmin ? RoleType.Admin : isSuperAdmin ? RoleType.SuperAdmin : RoleType.User;
}

/**
 * firstName/lastName straight off the Cognito ID token's given_name/
 * family_name claims — see the comment on CachedSession above for why
 * this exists as a fallback. No network call beyond fetchAuthSession
 * (same as deriveRole above), so this can't fail for server-side
 * reasons the way every user_* attempt did.
 */
async function deriveNameFromToken(): Promise<{firstName: string; lastName: string}> {
   const authSession = await fetchAuthSession();
   const claims = authSession.tokens?.idToken?.payload ?? {};

   return {
      firstName: typeof claims['given_name'] === 'string' ? claims['given_name'] : '',
      lastName: typeof claims['family_name'] === 'string' ? claims['family_name'] : '',
   };
}

/**
 * ADDED 2026-09-04 (Duane, re: the AWS 401 investigation): confirms
 * Cognito itself is reachable — distinct from lib/onlineState.ts's
 * probeReachability, which only proves the app's OWN origin (a static
 * asset request) is reachable. Those are two different AWS services;
 * isOnlineAtom flipping true is driven entirely by that origin-only
 * probe (see useAuthBootstrap.ts), so a device can look "online" while
 * Cognito's regional endpoint is still unreachable (asymmetric routing
 * right after a network switch, a captive portal with only some routes
 * open, DNS still propagating, ...). apolloClient.ts's authLink already
 * calls fetchAuthSession() on every request and lets it refresh an
 * expired access token under the hood when it can — this reuses that
 * exact call as a standalone yes/no reachability check, rather than
 * inventing a second way to ask the same question, so a caller (see
 * useOfflineSync.ts) can confirm a token is actually obtainable BEFORE
 * replaying a whole backlog of queued mutations into what might still
 * be a Cognito outage and 401ing every single one of them.
 *
 * Returns false for both "genuinely unreachable" and "reachable, but
 * the session/refresh token is actually dead" — the same ambiguity
 * authLink's own catch block already lives with (fetchAuthSession()
 * throws for both, with no reliable way to tell them apart from the
 * error alone). Either way the answer for a caller deciding whether to
 * send requests right now is the same: not yet.
 */
export async function checkAuthReachable(): Promise<boolean> {
   try {
      const session = await fetchAuthSession();
      return Boolean(session?.tokens?.accessToken);
   } catch {
      return false;
   }
}

/**
 * Backend-sourced user profile for the signed-in user — see the history
 * on CachedSession above for why this uses user_GetMyDetails rather than
 * user_ByCognitoSub. Returns null (rather than throwing) on any failure,
 * including the backend returning no user, so callers can fall back to
 * deriveNameFromToken without special-casing errors.
 *
 * fetchPolicy: 'network-only' because this is a fresh
 * bootstrap/sign-in check each time, not something that should serve a
 * stale cached result (or cache a failure).
 */
async function fetchMyUserProfile(): Promise<{
   firstName: string;
   lastName: string;
   employeeId: string;
   email: string;
} | null> {
   try {
      const {data} = await apolloClient.query({
         query: User_GetMyDetailsDocument,
         fetchPolicy: 'network-only',
      });
      const user = data?.user_GetMyDetails;
      if (!user) {
         console.warn('user_GetMyDetails returned no user — falling back to token-derived name.');
         return null;
      }

      return {
         firstName: user.firstName ?? '',
         lastName: user.lastName ?? '',
         employeeId: user.employeeId ?? '',
         email: user.email ?? '',
      };
   } catch (err) {
      console.warn('user_GetMyDetails failed — falling back to token-derived name.', err);
      return null;
   }
}

/**
 * Call once at app startup (see AuthProvider). Tries to verify the
 * session against Cognito; if that fails because of a network error
 * and we have a non-expired cached session, falls back to
 * 'offline-cached' instead of forcing a login screen.
 *
 * Either way, if the resolved role is the field role, this also
 * enables Apollo cache persistence to IndexedDB so wells/barrel-test
 * queries are available offline — see lib/apolloClient.ts. Admin/
 * SuperAdmin sessions never enable it.
 */
export const bootstrapAuthAtom = atom(null, async (get, set) => {
   set(authStatusAtom, 'loading');

   try {
      const role = await deriveRole();
      const user = await getCurrentUser();

      // Tries the backend profile lookup first; falls back to the ID
      // token's claims if it fails — see the comment on CachedSession
      // above.
      const profile = await fetchMyUserProfile();
      const {firstName, lastName} = profile ?? (await deriveNameFromToken());

      const session: CachedSession = {
         username: user.username,
         userId: user.userId,
         firstName,
         lastName,
         employeeId: profile?.employeeId ?? '',
         email: profile?.email ?? '',
         role,
         lastVerifiedAt: new Date().toISOString(),
      };
      set(cachedSessionAtom, session);

      if (isFieldRole(role)) {
         await enableOfflineCachePersistence();
      }

      set(authStatusAtom, 'authenticated');
      return;
   } catch {
      // Distinguish "not signed in" from "couldn't reach Cognito".
      // Amplify throws network errors as generic Error objects with
      // no recognizable auth error name; treat anything unexpected as
      // a possible connectivity issue and fall back to cache.
      const cached = get(cachedSessionAtom);
      const isOnline = get(isOnlineAtom);

      if (!isOnline && cached) {
         const age = Date.now() - new Date(cached.lastVerifiedAt).getTime();
         if (age < OFFLINE_GRACE_PERIOD_MS) {
            if (isFieldRole(cached.role)) {
               // Restores whatever was persisted from the last online
               // session so cached wells/barrel tests are readable now.
               await enableOfflineCachePersistence();
            }
            set(authStatusAtom, 'offline-cached');
            return;
         }
      }

      set(authStatusAtom, 'unauthenticated');
   }
});

export const signInAtom = atom(null, async (_get, set, {username, password}: {username: string; password: string}) => {
   // Devices are no longer restricted to a single concurrent login, so no
   // device id is collected/sent at sign-in anymore — see the matching
   // removal of deviceId from AdminUser/UserFormPanel/UsersAdminPage.
   const result = await amplifySignIn({username, password});
   if (result.isSignedIn) {
      const role = await deriveRole();
      const user = await getCurrentUser();

      const profile = await fetchMyUserProfile();
      const {firstName, lastName} = profile ?? (await deriveNameFromToken());

      const session: CachedSession = {
         username: user.username,
         userId: user.userId,
         firstName,
         lastName,
         employeeId: profile?.employeeId ?? '',
         email: profile?.email ?? '',
         role,
         lastVerifiedAt: new Date().toISOString(),
      };
      set(cachedSessionAtom, session);

      if (isFieldRole(role)) {
         await enableOfflineCachePersistence();
      }

      set(authStatusAtom, 'authenticated');
   }
   return result;
});

export const signOutAtom = atom(null, async (_get, set) => {
   try {
      await amplifySignOut();
   } finally {
      set(cachedSessionAtom, null);
      set(authStatusAtom, 'unauthenticated');
   }
});
